import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Question } from '../types/api';
import './QuestionBasedForm.css';

interface QuestionBasedFormProps {
  questions: Question[];
  onSubmit: (formData: Record<string, string>) => void;
  isSubmitting?: boolean;
  initialData?: Record<string, string>;
}

interface FormErrors {
  [key: string]: string;
}

const QuestionBasedForm: React.FC<QuestionBasedFormProps> = ({ 
  questions, 
  onSubmit, 
  isSubmitting = false,
  initialData = {}
}) => {
  // const navigate = useNavigate(); // For future use
  const [formData, setFormData] = useState<Record<string, string>>(initialData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [customInputs, setCustomInputs] = useState<Record<string, boolean>>({});

  // Convert questions to form fields - all questions are form fields since they come from your admin
  const formFields = questions
    .sort((a, b) => (a.order || 0) - (b.order || 0)); // Sort by order

  // Domain validation for email/website matching
  const validateDomainMatch = (emailValue: string, domainValue: string): boolean => {
    if (!emailValue || !domainValue) return true;
    
    const emailDomain = emailValue.split('@')[1];
    if (!emailDomain) return false;
    
    // Bypass validation for internal testing domains
    const allowedDomains = ['athenasol.ai', 'plumberseo.com'];
    if (allowedDomains.includes(emailDomain.toLowerCase())) {
      return true;
    }
    
    const normalizedDomain = domainValue.replace(/^https?:\/\//, '').replace(/^www\./, '').toLowerCase();
    const normalizedEmailDomain = emailDomain.replace(/^www\./, '').toLowerCase();
    
    return normalizedDomain === normalizedEmailDomain;
  };

  // Extract domain name without TLD for company name comparison
  const extractBaseDomain = (domain: string): string => {
    return domain.replace(/^https?:\/\//, '')
                 .replace(/^www\./, '')
                 .split('.')[0]
                 .toLowerCase();
  };

  // Normalize company name for comparison
  const normalizeCompanyName = (companyName: string): string => {
    return companyName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
      .replace(/\b(inc|llc|ltd|corp|corporation|company|co|llp|lp)\b/g, '') // Remove common business suffixes
      .trim();
  };

  // Check if company name is similar to domain
  const validateCompanyDomainMatch = (companyName: string, domain: string): boolean => {
    if (!companyName || !domain) return true;
    
    const normalizedCompany = normalizeCompanyName(companyName);
    const baseDomain = extractBaseDomain(domain);
    
    // If either is too short, be more lenient
    if (normalizedCompany.length < 3 || baseDomain.length < 3) return true;
    
    // Check exact matches first
    if (normalizedCompany === baseDomain) return true;
    
    // Check if company name is contained in domain or vice versa
    if (normalizedCompany.includes(baseDomain) || baseDomain.includes(normalizedCompany)) {
      return true;
    }
    
    // Check without common words
    const companyWithoutCommon = normalizedCompany.replace(/\b(the|a|an|and|of|for)\b/g, '').replace(/\s+/g, '');
    if (companyWithoutCommon.includes(baseDomain) || baseDomain.includes(companyWithoutCommon)) {
      return true;
    }
    
    // Check for partial matches (at least 70% similarity for longer names)
    if (normalizedCompany.length > 5 && baseDomain.length > 5) {
      const similarity = calculateSimilarity(normalizedCompany, baseDomain);
      return similarity > 0.7;
    }
    
    return false;
  };

  // Calculate string similarity (Levenshtein distance based)
  const calculateSimilarity = (str1: string, str2: string): number => {
    const maxLength = Math.max(str1.length, str2.length);
    if (maxLength === 0) return 1;
    
    const distance = levenshteinDistance(str1, str2);
    return (maxLength - distance) / maxLength;
  };

  // Levenshtein distance calculation
  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
    
    for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
    
    for (let j = 1; j <= str2.length; j++) {
      for (let i = 1; i <= str1.length; i++) {
        const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1,     // deletion
          matrix[j - 1][i] + 1,     // insertion
          matrix[j - 1][i - 1] + indicator // substitution
        );
      }
    }
    
    return matrix[str2.length][str1.length];
  };

  // Map question types to HTML input types
  const getInputType = (questionType: string): string => {
    switch (questionType.toLowerCase()) {
      case 'email':
        return 'email';
      case 'url':
      case 'website':
        return 'url';
      case 'phone':
      case 'tel':
        return 'tel';
      case 'number':
        return 'number';
      case 'openended':
      default:
        return 'text';
    }
  };

  // Validate individual field
  const validateField = (question: Question, value: string): string | null => {
    // All fields are required - user cannot leave any field empty
    if (!value.trim()) {
      return `${question.question_text} is required`;
    }

    // Email validation
    if (question.question_type === 'email' || question.question_id.includes('email')) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return 'Please enter a valid email address';
      }
    }

    // URL validation
    if (question.question_type === 'url' || question.question_type === 'website' || question.question_id.includes('website')) {
      const urlRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.([a-zA-Z]{2,}\.?)+$/;
      if (!urlRegex.test(value)) {
        return 'Please enter a valid website domain (e.g., example.com)';
      }
    }

    return null;
  };

  // Validate entire form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate each field
    formFields.forEach(question => {
      const error = validateField(question, formData[question.question_id] || '');
      if (error) {
        newErrors[question.question_id] = error;
      }
    });

    // Check for domain matching if both email and website fields exist
    const emailField = formFields.find(q => q.question_type === 'email' || q.question_id.includes('email'));
    const websiteField = formFields.find(q => q.question_type === 'url' || q.question_type === 'website' || q.question_id.includes('website'));
    const companyField = formFields.find(q => 
      q.question_id.includes('company') || 
      q.question_id.includes('business') || 
      q.question_text.toLowerCase().includes('company') ||
      q.question_text.toLowerCase().includes('business')
    );
    
    if (emailField && websiteField) {
      const emailValue = formData[emailField.question_id];
      const websiteValue = formData[websiteField.question_id];
      
      if (emailValue && websiteValue && !validateDomainMatch(emailValue, websiteValue)) {
        newErrors[emailField.question_id] = 'Email domain must match your website domain';
        newErrors[websiteField.question_id] = 'Website domain must match your email domain';
      }
    }

    // Check if company name matches domain names
    if (companyField && (emailField || websiteField)) {
      const companyValue = formData[companyField.question_id];
      
      if (companyValue) {
        let domainToCheck = '';
        let fieldToError = '';
        
        // Use website domain if available, otherwise use email domain
        if (websiteField && formData[websiteField.question_id]) {
          domainToCheck = formData[websiteField.question_id];
          fieldToError = companyField.question_id;
        } else if (emailField && formData[emailField.question_id]) {
          const emailDomain = formData[emailField.question_id].split('@')[1];
          if (emailDomain) {
            domainToCheck = emailDomain;
            fieldToError = companyField.question_id;
          }
        }
        
        if (domainToCheck && !validateCompanyDomainMatch(companyValue, domainToCheck)) {
          newErrors[fieldToError] = 'Company name should be related to your website/email domain';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (fieldName: string, value: string) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Additional domain validation check before submission
    const emailField = formFields.find(q => q.question_type === 'email' || q.question_id.includes('email'));
    const websiteField = formFields.find(q => q.question_type === 'url' || q.question_type === 'website' || q.question_id.includes('website'));
    
    if (emailField && websiteField) {
      const emailValue = formData[emailField.question_id];
      const websiteValue = formData[websiteField.question_id];
      
      if (emailValue && websiteValue && !validateDomainMatch(emailValue, websiteValue)) {
        alert('Please ensure your website domain matches your email domain before proceeding.');
        return;
      }
    }

    onSubmit(formData);
  };

  const handleMultipleChoiceChange = (questionId: string, selectedValue: string) => {
    if (selectedValue === 'other') {
      // Switch to custom input mode
      setCustomInputs(prev => ({ ...prev, [questionId]: true }));
      setFormData(prev => ({ ...prev, [questionId]: '' }));
    } else {
      // Regular option selected
      setCustomInputs(prev => ({ ...prev, [questionId]: false }));
      setFormData(prev => ({ ...prev, [questionId]: selectedValue }));
    }
    
    // Clear error for this field
    if (errors[questionId]) {
      setErrors(prev => ({ ...prev, [questionId]: '' }));
    }
  };

  const renderField = (question: Question) => {
    const value = formData[question.question_id] || '';
    const hasError = Boolean(errors[question.question_id]);
    const isRequired = true; // All fields are required
    const isCustomInput = customInputs[question.question_id] || false;

    switch (question.question_type.toLowerCase()) {
      case 'multiple_choice':
        const options = question.options || [];
        
        if (isCustomInput) {
          // Show custom input with option to go back to dropdown
          return (
            <div className="custom-input-container">
              <input
                type="text"
                id={question.question_id}
                value={value}
                onChange={(e) => handleInputChange(question.question_id, e.target.value)}
                placeholder={question.question_placeholder || 'Enter your custom response...'}
                className={`form-input ${hasError ? 'error' : ''}`}
                required={isRequired}
              />
              <button
                type="button"
                onClick={() => {
                  setCustomInputs(prev => ({ ...prev, [question.question_id]: false }));
                  setFormData(prev => ({ ...prev, [question.question_id]: '' }));
                }}
                className="back-to-dropdown-btn"
              >
                ← Back to options
              </button>
            </div>
          );
        }

        // Show dropdown with options + "Other"
        return (
          <select
            id={question.question_id}
            value={value}
            onChange={(e) => handleMultipleChoiceChange(question.question_id, e.target.value)}
            className={`form-select ${hasError ? 'error' : ''}`}
            required={isRequired}
          >
            <option value="">{question.question_placeholder || 'Select an option'}</option>
            {options.map((option: string, index: number) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
            <option value="other">Other (specify)</option>
          </select>
        );

      case 'select':
      case 'dropdown':
        const selectOptions = question.options || [];
        return (
          <select
            id={question.question_id}
            value={value}
            onChange={(e) => handleInputChange(question.question_id, e.target.value)}
            className={`form-select ${hasError ? 'error' : ''}`}
            required={isRequired}
          >
            <option value="">{question.question_placeholder || `Select an option`}</option>
            {selectOptions.map((option: string, index: number) => (
              <option key={index} value={option.toLowerCase().replace(/\s+/g, '_')}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'textarea':
      case 'longtext':
        return (
          <textarea
            id={question.question_id}
            value={value}
            onChange={(e) => handleInputChange(question.question_id, e.target.value)}
            placeholder={question.question_placeholder}
            className={`form-input ${hasError ? 'error' : ''}`}
            required={isRequired}
            rows={4}
          />
        );

      default:
        return (
          <input
            type={getInputType(question.question_type)}
            id={question.question_id}
            value={value}
            onChange={(e) => handleInputChange(question.question_id, e.target.value)}
            placeholder={question.question_placeholder}
            className={`form-input ${hasError ? 'error' : ''}`}
            required={isRequired}
          />
        );
    }
  };

  // Check if domain validation is failing
  const emailField = formFields.find(q => q.question_type === 'email' || q.question_id.includes('email'));
  const websiteField = formFields.find(q => q.question_type === 'url' || q.question_type === 'website' || q.question_id.includes('website'));
  const hasDomainMismatch = emailField && websiteField && 
    formData[emailField.question_id] && formData[websiteField.question_id] &&
    !validateDomainMatch(formData[emailField.question_id], formData[websiteField.question_id]);

  if (formFields.length === 0) {
    return (
      <div className="question-form-container">
        <div className="error-container">
          <h2 className="form-title text-center">
            See How Your Business Ranks in AI Search
          </h2>
          <p className="form-subtitle text-center">
            Enter your information below to see how your business appears in AI 
            search results compared to your competitors
          </p>
          <p className="error-message text-center">
            No form questions found. Please configure form questions with field_name properties in your admin panel.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="question-form-container">
      <form onSubmit={handleSubmit} className="question-form">
        <div className="form-header">
          <h2 className="form-title text-center">
            See How Your Business Ranks in AI Search
          </h2>
          <p className="form-subtitle text-center">
            Enter your information below to see how your business appears in AI 
            search results compared to your competitors
          </p>
          <p className="required-notice text-center">
            <span className="required-asterisk">*</span> All fields are required
          </p>
        </div>

        {formFields.map((question) => (
          <div key={question.question_id} className="form-group">
            <label htmlFor={question.question_id} className="form-label">
              {question.question_text} *
            </label>
            {renderField(question)}
            {errors[question.question_id] && (
              <span className="error-message">{errors[question.question_id]}</span>
            )}
            
            {/* Show helper text if available */}
            {question.question_helper_text && !errors[question.question_id] && (
              <div className="helper-text">
                {question.question_helper_text}
              </div>
            )}
            
            {/* Show domain mismatch warning for website field */}
            {(question.question_type === 'url' || question.question_type === 'website' || question.question_id.includes('website')) && 
             emailField && hasDomainMismatch && !errors[question.question_id] && (
              <div className="warning-message">
                ⚠️ Website domain ({formData[question.question_id]}) does not match email domain ({formData[emailField.question_id]?.split('@')[1]})
              </div>
            )}
          </div>
        ))}

        <button
          type="submit"
          disabled={isSubmitting || Boolean(hasDomainMismatch)}
          className="btn btn-primary submit-btn"
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              Analyzing...
            </>
          ) : Boolean(hasDomainMismatch) ? (
            'Fix Domain Mismatch to Continue'
          ) : (
            'Check My AI Ranking'
          )}
        </button>
      </form>
    </div>
  );
};

export default QuestionBasedForm;
